import { ReactNode, createContext, useContext } from 'react';
import type { AlertInstance } from '../../api/types';
import Badge from './Badge';
import EvidenceList, { formatValue } from './EvidenceList';

interface Props {
  alert: AlertInstance;
  compact?: boolean;
  /** The headline sentence is already shown above (Alerts detail): render only the stat tiles and extras. */
  hideHeadline?: boolean;
}

const n1 = (v: unknown) => (typeof v === 'number' ? v.toFixed(1) : String(v ?? '—'));
const n0 = (v: unknown) => (typeof v === 'number' ? v.toFixed(0) : String(v ?? '—'));
/** Whole days since the last check-in, from its timestamp (the frozen `days_silent` only if the timestamp is missing). */
function checkinAge(p: Record<string, any>): string | undefined {
  const t = p.last_checkin_at ? new Date(p.last_checkin_at).getTime() : NaN;
  if (!Number.isNaN(t)) return `${days(Math.max(0, Math.floor((Date.now() - t) / 86_400_000)))} ago`;
  return p.days_silent != null ? `${days(p.days_silent)} ago` : undefined;
}
const days = (v: unknown) => `${n0(v)} day${v === 1 ? '' : 's'}`;

/** True when the page already shows the headline sentence (the Alerts detail title), so the stats stand alone. */
const HeadlineShown = createContext(false);

type Stat = { label: string; value: ReactNode; tone?: 'primary' | 'danger' | 'success' | 'neutral'; note?: string };

function StatBlock({ label, value, tone = 'neutral', note }: Stat) {
  const valueColor = tone === 'danger' ? 'text-danger-ink'
    : tone === 'success' ? 'text-success'
    : tone === 'primary' ? 'text-primary'
    : 'text-ink';
  return (
    <div className="rounded-[14px] bg-evidence p-3.5">
      <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">{label}</p>
      <p className={`mt-2 font-mono text-2xl font-semibold leading-none tracking-[-0.02em] ${valueColor}`}>{value}</p>
      {note && <p className="mt-1.5 text-xs text-muted">{note}</p>}
    </div>
  );
}

function MilestoneDots({ fired }: { fired: number[] }) {
  const milestones = [25, 50, 75, 100];
  return (
    <div className="mt-2 flex gap-2">
      {milestones.map((m) => {
        const hit = fired.includes(m);
        return (
          <span key={m}
                className={`rounded-full px-2 py-0.5 text-xs ${hit ? 'bg-navy text-white dark:bg-mist-fixed dark:text-navy' : 'bg-track text-muted'}`}>
            {m}%
          </span>
        );
      })}
    </div>
  );
}

/** Headline sentence + stat blocks, the shape every rule renders in. */
function Detail({ headline, stats, children }: { headline: string; stats?: Stat[]; children?: ReactNode }) {
  const shown = useContext(HeadlineShown);
  return (
    <div>
      {!shown && <h4 className="text-base font-semibold text-ink">{headline}</h4>}
      {stats && stats.length > 0 && (
        <div className={`${shown ? 'my-[18px]' : 'mt-3'} grid gap-2.5 sm:grid-cols-3`}>
          {stats.map((s) => <StatBlock key={s.label} {...s} />)}
        </div>
      )}
      {children}
    </div>
  );
}

export default function AlertPayloadDetail({ alert, compact = false, hideHeadline = false }: Props) {
  if (hideHeadline && !compact) {
    return <HeadlineShown.Provider value><AlertPayloadDetail alert={alert} /></HeadlineShown.Provider>;
  }
  const { rule_code, payload } = alert;
  const p = payload as Record<string, any>;
  const line = (text: string) => <p className="text-sm">{text}</p>;

  switch (rule_code) {
    case 'academic_decline_subject': {
      const headline = `${p.subject ?? 'Subject'} score dropped ${n1(p.delta_pct)}%`;
      if (compact) return <p className="text-sm">
        <span className="font-medium">{p.subject ?? 'Subject'}</span>
        {' '}avg dropped <span className="text-danger font-medium">{n1(p.delta_pct)}%</span>
        {' '}(from {n1(p.prior_avg)}% to {n1(p.current_score)}%).
      </p>;
      return <Detail headline={headline} stats={[
        { label: `Prior ${p.samples ?? 4}-attempt avg`, value: `${n1(p.prior_avg)}%`, note: typeof p.samples === 'number' ? `over ${p.samples} attempts` : undefined },
        { label: 'Latest score', value: `${n1(p.current_score)}%`, tone: 'danger' },
        { label: 'Drop', value: `${n1(p.delta_pct)}%`, tone: 'danger' },
      ]} />;
    }

    case 'repeated_chapter_fail': {
      const scores: number[] = Array.isArray(p.recent_scores) ? p.recent_scores : [];
      const fails = p.consecutive_fails ?? scores.length;
      if (compact) return <p className="text-sm">
        <span className="font-medium">{p.chapter_name ?? 'Chapter'}</span>{' '}— {fails} consecutive fails.
      </p>;
      return <Detail headline={`${p.chapter_name ?? 'Chapter'} — ${fails} consecutive fails`}>
        <p className="mt-2 text-sm text-bodydark2">Recent scores:</p>
        <div className="mt-1 flex flex-wrap gap-2">
          {scores.map((s, i) => <Badge key={i} tone="danger">{s}%</Badge>)}
        </div>
      </Detail>;
    }

    case 'missed_milestone': {
      const isLapsed = p.status_at_fire === 'lapsed';
      const d = p.silent_days_observed;
      const milestones: number[] = Array.isArray(p.milestones_fired) ? p.milestones_fired : [];
      const headline = isLapsed
        ? `Commitment lapsed${d != null ? ` — silent for ${d} days` : ''}.`
        : `No progress for ${d ?? 0} days on the active commitment.`;
      if (compact) return line(headline);
      return <Detail headline={headline}>
        {p.kind && <p className="mt-1 text-sm text-bodydark2">Kind: {String(p.kind).replace(/_/g, ' ')}{p.target_value != null ? ` · target ${p.target_value}` : ''}</p>}
        <p className="mt-3 text-xs uppercase tracking-wider text-bodydark2">Milestones reached</p>
        <MilestoneDots fired={milestones} />
      </Detail>;
    }

    case 'wellbeing_red_flag': {
      const states: string[] = Array.isArray(p.consecutive_states) ? p.consecutive_states : [];
      if (compact) return line(`Wellbeing red flag: ${states.join(', ')}.`);
      return <Detail headline="Two consecutive check-ins flagged.">
        <p className="mt-2 text-sm">
          States: {states.map((s, i) => (
            <span key={i} className="ml-2 inline-flex items-center gap-1 rounded bg-stroke px-2 py-0.5 text-xs dark:bg-meta-4">
              {String(s).replace(/_/g, ' ')}
            </span>
          ))}
        </p>
        <p className="mt-3 text-sm italic text-bodydark2">Treat this conversation with care.</p>
      </Detail>;
    }

    case 'wellbeing_silence': {
      const headline = `No wellbeing check-in for ${days(p.days_silent)}.`;
      if (compact) return line(headline);
      return <Detail headline={headline} stats={[
        { label: 'Days silent', value: n0(p.days_silent), tone: 'danger' },
        { label: 'Last check-in', value: formatValue(p.last_checkin_at), note: checkinAge(p) },
        { label: 'Last state', value: p.last_state ? String(p.last_state).replace(/_/g, ' ') : '—' },
      ]} />;
    }

    case 'engagement_drop': {
      if (p.trigger === 'silent') {
        const headline = `No activity on the app for ${days(p.days_silent)}.`;
        if (compact) return line(headline);
        return <Detail headline={headline} stats={[{ label: 'Days silent', value: n0(p.days_silent), tone: 'danger' }]} />;
      }
      const headline = `Study time fell to ${n0(p.recent_minutes)} minutes this week, from a usual ${n0(p.baseline_minutes)}.`;
      if (compact) return line(headline);
      return <Detail headline={headline} stats={[
        { label: 'This week', value: `${n0(p.recent_minutes)} min`, tone: 'danger' },
        { label: 'Usual week', value: `${n0(p.baseline_minutes)} min` },
      ]} />;
    }

    case 'late_night_study': {
      const dates: string[] = Array.isArray(p.sample_event_dates) ? p.sample_event_dates : [];
      const headline = `Studied late at night on ${p.late_nights ?? dates.length} night${p.late_nights === 1 ? '' : 's'} this week.`;
      if (compact) return line(headline);
      return <Detail headline={headline}>
        {dates.length > 0 && <p className="mt-2 text-sm text-bodydark2">Nights: {dates.map((d) => formatValue(d)).join(', ')}</p>}
      </Detail>;
    }

    case 'peer_gap_persistent': {
      const bottom = p.direction !== 'top';
      const headline = `${p.subject_name ?? 'Subject'}: in the ${bottom ? 'bottom' : 'top'} 10% of the class for ${p.weeks_in_decile ?? '?'} weeks running.`;
      if (compact) return line(headline);
      return <Detail headline={headline} stats={[
        { label: 'Latest percentile', value: `${n0(p.latest_percentile)}th`, tone: bottom ? 'danger' : 'success' },
        { label: 'Weeks in a row', value: n0(p.weeks_in_decile) },
        { label: 'Class size', value: n0(p.cohort_size) },
      ]} />;
    }

    case 'class_rank_drop': {
      const headline = `Class standing fell ${n0(p.drop_pct)} points, from the ${n0(p.baseline_percentile)}th to the ${n0(p.current_percentile)}th percentile.`;
      if (compact) return line(headline);
      return <Detail headline={headline} stats={[
        { label: 'Usual percentile', value: `${n0(p.baseline_percentile)}th` },
        { label: 'Now', value: `${n0(p.current_percentile)}th`, tone: 'danger' },
        { label: 'Class size', value: n0(p.cohort_size) },
      ]}>
        {p.sample_weeks != null && <p className="mt-2 text-xs text-bodydark2">Usual percentile is the average of the last {p.sample_weeks} weeks.</p>}
      </Detail>;
    }

    case 'streak_break': {
      const headline = `A ${p.previous_length ?? '?'}-day study streak ended${p.broken_at ? ` on ${formatValue(p.broken_at)}` : ''}${p.days_missed != null ? `, after ${days(p.days_missed)} missed` : ''}.`;
      if (compact) return line(headline);
      return <Detail headline={headline} stats={[
        { label: 'Streak length', value: `${n0(p.previous_length)} days` },
        { label: 'Days missed', value: n0(p.days_missed), tone: 'danger' },
      ]} />;
    }

    case 'study_plan_skipped': {
      const subjects: string[] = Array.isArray(p.subjects_skipped) ? p.subjects_skipped : [];
      const headline = `Skipped ${p.skipped_count ?? subjects.length} study-plan item${p.skipped_count === 1 ? '' : 's'}${p.week_start ? ` in the week of ${p.week_start}` : ''}.`;
      if (compact) return line(headline);
      return <Detail headline={headline}>
        {subjects.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">{subjects.map((s, i) => <Badge key={i} tone="warning">{s}</Badge>)}</div>
        )}
      </Detail>;
    }

    case 'ai_overreliance': {
      const subs: string[] = Array.isArray(p.declining_subjects) ? p.declining_subjects : [];
      const headline = `AI help rose to about ${n1(p.recent_daily)} uses a day (usually ${n1(p.baseline_daily)}) while scores fell${subs.length ? ` in ${subs.join(', ')}` : ''}.`;
      if (compact) return line(headline);
      return <Detail headline={headline} stats={[
        { label: 'AI uses per day, now', value: n1(p.recent_daily), tone: 'danger' },
        { label: 'AI uses per day, usual', value: n1(p.baseline_daily) },
        { label: 'Subjects declining', value: subs.length ? subs.join(', ') : '—' },
      ]} />;
    }

    case 'ai_cheating_suspected': {
      const events: Array<Record<string, unknown>> = Array.isArray(p.ai_events) ? p.ai_events : [];
      const window: string[] = Array.isArray(p.window) ? p.window : [];
      const headline = `AI features were used during an exam${events.length ? ` (${events.length} time${events.length === 1 ? '' : 's'})` : ''}.`;
      if (compact) return line(headline);
      return <Detail headline={headline}>
        {p.clause && <p className="mt-1 text-sm text-bodydark2">Why it fired: {String(p.clause).replace(/_/g, ' ')}</p>}
        {window.length === 2 && <p className="mt-1 text-sm text-bodydark2">Exam window: {formatValue(window[0])} to {formatValue(window[1])}</p>}
        {events.length > 0 && (
          <ul className="mt-2 space-y-1 text-sm">
            {events.map((e, i) => (
              <li key={i}>{formatValue(e.created_at)} · {String(e.feature ?? e.event_type ?? 'AI feature').replace(/_/g, ' ')}</li>
            ))}
          </ul>
        )}
      </Detail>;
    }

    default:
      // A rule this build does not know yet: still readable, never raw JSON.
      if (compact) {
        const first = Object.entries(p).find(([, v]) => v != null && typeof v !== 'object');
        return line(first ? `${first[0].replace(/_/g, ' ')}: ${formatValue(first[1])}` : 'See the alert for details.');
      }
      return <EvidenceList data={p} className="mt-1" />;
  }
}
